import type { ListItemProps } from "@/components/ListItem/ListItem";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { getCurrentPrimaryEmployment } from "@/features/person/utils/utils";
import { formatDisplayCurrency } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import type { Path, useNavigate } from "@/router";
import { PostStage } from "@/pages/_layouts/jobs/types";
import { ProgramStatus, ProgramTypeName } from "@/types";
import { minimumCompensationOptions } from "../_constants";

export const ACTIVELY_LOOKING = "Actively looking for a new position";

export function canAccessCareerOverview({
  canViewCareerPreferences,
  canViewCompensationProfiles,
  canManageJobPosts
}: {
  canViewCareerPreferences: boolean;
  canViewCompensationProfiles: boolean;
  canManageJobPosts: boolean;
}): boolean {
  return canManageJobPosts || canViewCareerPreferences || canViewCompensationProfiles;
}

export type CareerOverviewEmployment = {
  id?: string;
  isPrimary?: boolean | null;
  title?: string | null;
  formattedTitle?: string | null;
  jobClassification?: string | null;
  startedOn?: string | null;
  endedOn?: string | null;
  facility?: { id?: string; name?: string | null } | null;
};

export type CareerOverviewSectionConsultant = {
  id: string;
  name: string;
};

/** Person fields Career Overview actually reads from GetCareerOverview. */
export type CareerOverviewPerson = {
  pgaId: string;
  employments?: CareerOverviewEmployment[] | null;
  careerInfo?: {
    hasCompensationProfile?: boolean | null;
    contactLastModifiedCompensationProfile?: string | null;
    hasJobPreferences?: boolean | null;
    jobPreferencesLastModified?: string | null;
    currentEmployerContainsGolfDirector?: boolean | null;
  } | null;
  primarySection?: {
    id?: string;
    name?: string | null;
    careerConsultants?: CareerOverviewSectionConsultant[] | null;
  } | null;
  latestProgram?: {
    id?: string;
    status?: string | null;
    programType?: { id?: string; name?: string | null } | null;
    band?: { id?: string; classification?: { id?: string; name?: string | null } | null } | null;
  } | null;
};

export type CareerOverviewNavigate = ReturnType<typeof useNavigate>;

function isActiveMemberOrAssociate(person: CareerOverviewPerson): boolean {
  const programTypeName = person.latestProgram?.programType?.name;
  const status = person.latestProgram?.status;
  return (
    (programTypeName === ProgramTypeName.Member || programTypeName === ProgramTypeName.Associate) &&
    (status === ProgramStatus.ACTIVE || status === ProgramStatus.SUSPENDED)
  );
}

export type CareerOverviewJobPreferences = {
  id: string;
  jobCompensationMinimum?: string | number | null;
  jobSearchStatus?: string | null;
  optedInToJobSeekerEmails?: boolean | null;
  updatedAt?: string | null;
};

export type CareerOverviewJobApplication = {
  id: string;
  appliedOn?: string | null;
  active?: boolean | null;
  reviewScoreAverage?: number | null;
  jobPost?: { id: string; name?: string | null; facility?: { id: string; name?: string | null } | null } | null;
};

export type CareerOverviewCompensationProfile = {
  id: string;
  isSnapshot?: boolean | null;
  updatedAt?: string | null;
  contactLastModifiedDate?: string | null;
  lastModificationSource?: string | null;
  basePay?: number | null;
  basePayType?: string | null;
  totalCompensation?: number | null;
  currentEmployerContainsGolfDirector?: boolean | null;
};

export type CareerOverviewGovernanceRole = {
  id: string;
  status?: string | null;
  title?: { id: string; title?: string | null } | null;
  facility?: { id: string; name?: string | null } | null;
};

/** Open/in-flight employer job stages for Career Overview count (PostStage display values). */
export const CAREER_OVERVIEW_OPEN_JOB_STAGES = [
  PostStage.ACTIVE,
  PostStage.PENDING,
  PostStage.IN_REVIEW,
  PostStage.ASSIST,
  PostStage.DRAFT
] as const;

function formatCurrencyAmount(amount?: number | null): string | undefined {
  if (amount == null) return undefined;
  return `$${formatDisplayCurrency(amount, 0)}`;
}

function formatCompensationMinimum(value?: string | number | null): string {
  if (value == null || value === "") return "No Minimum";
  const asString = String(value);
  const match = minimumCompensationOptions.find((option) => option.value === asString);
  if (match) return match.label;
  const numeric = Number(value);
  if (Number.isNaN(numeric) || numeric === 0) return "No Minimum";
  return `$${numeric.toLocaleString("en-US")}`;
}

function shortSearchStatus(status?: string | null): string {
  if (!status) return "Not set";
  if (status.startsWith("Actively")) return "Actively looking";
  if (status.startsWith("Passively")) return "Passively looking";
  if (status.startsWith("Not looking")) return "Not looking";
  return status;
}

function buildCompensationProfileTooltip(
  compensationProfile?: CareerOverviewCompensationProfile | null,
  contactLastModifiedFallback?: string | null
) {
  const lastModifiedByPerson = compensationProfile?.contactLastModifiedDate || contactLastModifiedFallback || null;
  const lastModifiedOverall = compensationProfile?.updatedAt || null;

  if (!lastModifiedByPerson && !lastModifiedOverall) {
    return undefined;
  }

  const source = compensationProfile?.lastModificationSource || "Unknown";
  const overallLabel = source === "Person" ? "Overall Last Modified" : `Last Modified by ${source}`;

  return {
    supportingText: (
      <div className="flex w-full flex-col items-start gap-2 text-body-medium">
        <div>
          <div className="text-title-small">Last Modified by Person</div>
          <div>{lastModifiedByPerson ? toFormat(lastModifiedByPerson, "MMM d, yyyy") : "--"}</div>
        </div>
        {lastModifiedOverall && (
          <div>
            <div className="text-title-small">{overallLabel}</div>
            <div>{toFormat(lastModifiedOverall, "MMM d, yyyy")}</div>
          </div>
        )}
      </div>
    )
  };
}

function navigateTo(pgaId: string, path: Path, navigate?: CareerOverviewNavigate) {
  return navigate ? () => navigate(path, { params: { pgaId } }) : undefined;
}

function getPrimaryOrCurrentEmployment(
  employments: CareerOverviewEmployment[] = []
): CareerOverviewEmployment | undefined {
  return getCurrentPrimaryEmployment(employments);
}

export function getCurrentCompensationProfile(
  profiles: CareerOverviewCompensationProfile[] = []
): CareerOverviewCompensationProfile | undefined {
  return profiles.find((profile) => !profile.isSnapshot);
}

export function getActiveGovernanceRoles(roles: CareerOverviewGovernanceRole[] = []): CareerOverviewGovernanceRole[] {
  return roles.filter((role) => (role.status || "").toUpperCase() === "ACTIVE");
}

export function buildCareerOverviewMetrics({
  person,
  jobPreferences,
  totalActiveJobApplications,
  canViewPersonCareer,
  canViewCareerPreferences,
  canManageJobPosts,
  canViewCompensationProfiles,
  canViewJobPostings,
  openJobPostCount,
  activeGovernanceRoleCount,
  compensationProfile
}: {
  person: CareerOverviewPerson;
  jobPreferences?: CareerOverviewJobPreferences | null;
  totalActiveJobApplications?: number | null;
  canViewPersonCareer: boolean;
  canViewCareerPreferences: boolean;
  canManageJobPosts: boolean;
  canViewCompensationProfiles: boolean;
  canViewJobPostings: boolean;
  openJobPostCount?: number;
  activeGovernanceRoleCount?: number;
  compensationProfile?: CareerOverviewCompensationProfile | null;
}): MetricCardType<Path>[] {
  const pgaId = person.pgaId;
  const metrics: MetricCardType<Path>[] = [];

  if (canViewPersonCareer) {
    const primaryEmployment = getPrimaryOrCurrentEmployment(person.employments ?? undefined);
    const isUnemployed = isActiveMemberOrAssociate(person) && !primaryEmployment;
    const employmentTitle = primaryEmployment
      ? primaryEmployment.jobClassification
        ? `${primaryEmployment.formattedTitle || primaryEmployment.title} (${primaryEmployment.jobClassification})`
        : primaryEmployment.formattedTitle || primaryEmployment.title
      : undefined;
    const employmentStartDate = primaryEmployment?.startedOn ? toFormat(primaryEmployment.startedOn) : null;

    metrics.push({
      label: employmentTitle || "Employment",
      value: isUnemployed ? "Unemployed" : primaryEmployment?.facility?.name || "No primary employment",
      iconName: "business_center",
      variant: isUnemployed ? "warn" : undefined,
      ...(employmentStartDate && {
        tooltip: {
          title: "Since",
          supportingText: employmentStartDate
        }
      }),
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/employment", params: { pgaId } }
      }),
      testId: "career-overview-metric-employment"
    });

    metrics.push({
      label: "Active Governance Roles",
      value: String(activeGovernanceRoleCount ?? 0),
      iconName: "store",
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/governance-roles", params: { pgaId } }
      }),
      testId: "career-overview-metric-governance-roles"
    });
  }

  if (canManageJobPosts) {
    metrics.push({
      label: "Active Applications",
      value: String(totalActiveJobApplications ?? 0),
      iconName: "business_center",
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/job-applications", params: { pgaId } }
      }),
      testId: "career-overview-metric-active-applications"
    });
  }

  if (canViewCareerPreferences) {
    const status = jobPreferences?.jobSearchStatus;
    const preferencesModifiedAt = jobPreferences?.updatedAt || person.careerInfo?.jobPreferencesLastModified || null;
    metrics.push({
      label: "Job Search Status",
      value: shortSearchStatus(status),
      iconName: "business_center",
      ...(preferencesModifiedAt && {
        tooltip: {
          title: "Last Modified",
          supportingText: toFormat(preferencesModifiedAt, "MMM d, yyyy")
        }
      }),
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/job-preferences", params: { pgaId } }
      }),
      testId: "career-overview-metric-search-status"
    });
  }

  if (canViewCompensationProfiles) {
    const hasProfile = !!compensationProfile || !!person.careerInfo?.hasCompensationProfile;
    const tooltip = buildCompensationProfileTooltip(
      compensationProfile,
      person.careerInfo?.contactLastModifiedCompensationProfile
    );
    metrics.push({
      label: "Compensation Profile",
      value: hasProfile ? "Yes" : "None",
      iconName: "paid",
      ...(tooltip && { tooltip }),
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/compensation", params: { pgaId } }
      }),
      testId: "career-overview-metric-compensation"
    });
  }

  if (canViewJobPostings) {
    metrics.push({
      label: "Open Job Postings as Employer",
      value: String(openJobPostCount ?? 0),
      iconName: "campaign",
      ...(pgaId && {
        link: { to: "/people/:pgaId/career/job-postings", params: { pgaId } }
      }),
      testId: "career-overview-metric-job-postings"
    });
  }

  return metrics;
}

function careerConsultantOverviewOptions(
  careerConsultants: CareerOverviewSectionConsultant[] | null | undefined
): ListItemProps[] {
  const consultantNames = (careerConsultants ?? []).map((consultant) => consultant.name).filter(Boolean);

  if (consultantNames.length === 0) {
    return [];
  }

  return [
    {
      iconName: "support_agent",
      overline: "Career Consultants",
      headline: consultantNames.join(", ")
    }
  ];
}

export function buildPreferencesOverviewOptions(
  jobPreferences: CareerOverviewJobPreferences | null | undefined,
  pgaId: string,
  navigate?: CareerOverviewNavigate,
  careerConsultants?: CareerOverviewSectionConsultant[] | null
): ListItemProps[] {
  const action = navigateTo(pgaId, "/people/:pgaId/career/job-preferences", navigate);
  const consultantOptions = careerConsultantOverviewOptions(careerConsultants);

  if (!jobPreferences) {
    return [
      {
        iconName: "business_center",
        overline: "Job Preferences",
        headline: "Not created",
        action
      },
      ...consultantOptions
    ];
  }

  const options: ListItemProps[] = [
    {
      iconName: "mail",
      overline: "Email Notifications",
      headline: jobPreferences.optedInToJobSeekerEmails ? "Opted in" : "Opted out"
    },
    {
      iconName: "paid",
      overline: "Compensation Minimum",
      headline: formatCompensationMinimum(jobPreferences.jobCompensationMinimum)
    },
    ...consultantOptions
  ];

  options[0].action = action;
  return options;
}

export function buildApplicationsOverviewOptions(
  applications: CareerOverviewJobApplication[] = [],
  totalApplications: number | null | undefined,
  totalActive: number | null | undefined,
  pgaId: string,
  navigate?: CareerOverviewNavigate
): ListItemProps[] {
  const action = navigateTo(pgaId, "/people/:pgaId/career/job-applications", navigate);
  const recent = [...applications]
    .sort((a, b) => new Date(b.appliedOn || 0).getTime() - new Date(a.appliedOn || 0).getTime())
    .slice(0, 3);
  const scoredAverages = applications
    .map((app) => app.reviewScoreAverage)
    .filter((score): score is number => typeof score === "number");
  const averageReviewRating = scoredAverages.length
    ? (scoredAverages.reduce((sum, score) => sum + score, 0) / scoredAverages.length).toFixed(1)
    : "0";

  const options: ListItemProps[] = [];

  if (recent.length === 0) {
    options.push({
      iconName: "inbox",
      overline: "Recent Applications",
      headline: "None"
    });
  } else {
    recent.forEach((app) => {
      options.push({
        iconName: "article",
        overline: app.active ? "Active application" : "Application",
        headline: app.jobPost?.name || "Untitled job",
        supportingText: [
          app.jobPost?.facility?.name,
          app.appliedOn ? `Applied ${toFormat(app.appliedOn, "MMM d, yyyy")}` : null,
          typeof app.reviewScoreAverage === "number" ? `Avg review ${app.reviewScoreAverage.toFixed(1)}` : null
        ]
          .filter(Boolean)
          .join(" · ")
      });
    });
  }

  options.push({
    iconName: "description",
    overline: "Applications",
    headline: `${totalActive ?? 0} active / ${totalApplications ?? applications.length} total`
  });

  const totalCount = totalApplications ?? applications.length;
  if (totalCount > 0) {
    options.push({
      iconName: "rate_review",
      overline: "Average Review Rating",
      headline: averageReviewRating
    });
  }

  options[0].action = action;
  return options;
}

export function buildCompensationOverviewOptions(
  person: CareerOverviewPerson,
  profile: CareerOverviewCompensationProfile | null | undefined,
  navigate?: CareerOverviewNavigate
): ListItemProps[] {
  const pgaId = person.pgaId;
  const action = pgaId ? navigateTo(pgaId, "/people/:pgaId/career/compensation", navigate) : undefined;
  const hasProfile = !!profile || !!person.careerInfo?.hasCompensationProfile;

  if (!hasProfile) {
    return [
      {
        iconName: "paid",
        overline: "Compensation Profile",
        headline: "Not created",
        action
      }
    ];
  }

  const options: ListItemProps[] = [];

  if (profile?.basePay != null || profile?.totalCompensation != null) {
    options.push({
      iconName: "payments",
      overline: "Compensation",
      headline: [
        profile.basePay != null ? `Base ${formatCurrencyAmount(profile.basePay)}` : null,
        profile.totalCompensation != null ? `Total ${formatCurrencyAmount(profile.totalCompensation)}` : null
      ]
        .filter(Boolean)
        .join(" · ")
    });
  }

  if (profile?.basePayType) {
    options.push({
      iconName: "paid",
      overline: "Base Pay Type",
      headline: profile.basePayType
    });
  }

  const hasDirector =
    profile?.currentEmployerContainsGolfDirector ?? person.careerInfo?.currentEmployerContainsGolfDirector;
  if (hasDirector != null) {
    options.push({
      iconName: "location_on",
      overline: "Facility Has Director of Golf",
      headline: hasDirector ? "Yes" : "No"
    });
  }

  if (options.length === 0) {
    return [
      {
        iconName: "paid",
        overline: "Compensation Profile",
        headline: "On file",
        action
      }
    ];
  }

  options[0].action = action;
  return options;
}
